import { ReactElement, useMemo } from 'react';

import { useConnection } from 'modules/auth/hooks/useConnection';
import { BaseIntro, BaseIntroProps } from 'modules/common/components/BaseIntro';
import { DECIMAL_PLACES_SHORT } from 'modules/common/const';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetLiquidStakingTokenQuery } from 'modules/liquidStaking/actions/getLiquidStakingToken';
import { useGetLiquidStakingAPY } from 'modules/liquidStaking/hooks/useGetLiquidStakingAPY';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';

import { translation } from './translation';

export function Intro(): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { cacheTime } = useConnection();

  const { poolAddress } = useLiquidStakingPool();
  const { data } = useGetLiquidStakingTokenQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const { apy, isLoading } = useGetLiquidStakingAPY(poolAddress);

  const stats: BaseIntroProps['stats'] = useMemo(() => {
    return [
      {
        value: data?.totalAmount
          ? data.totalAmount.toFormat(DECIMAL_PLACES_SHORT)
          : undefined,
        label: t(keys.total, { token: t(keys.tokens.stakeToken) }),
      },
      {
        value:
          apy && !isLoading
            ? t(keys.upTo, {
                apy: apy.decimalPlaces(DECIMAL_PLACES_SHORT).toFormat(),
              })
            : undefined,
        label: t(keys.apy),
      },
      {
        value: t(keys.defi),
        label: t(keys.opportunities),
      },
    ];
  }, [
    apy,
    data,
    isLoading,
    keys.apy,
    keys.defi,
    keys.opportunities,
    keys.tokens.stakeToken,
    keys.total,
    keys.upTo,
    t,
  ]);

  return (
    <BaseIntro
      stats={stats}
      title={t(keys.title, { token: t(keys.tokens.stakeToken) }, true)}
    />
  );
}
